import { WEBCHAT_MESSAGE_TYPE } from '@servicetitan/web-chat-types';
import { WEBCHAT_EXPAND_AVAILABILITY } from './expandAvailability';
import {
  attachIframe,
  buildIframeUrl,
  createFallback,
  createIframe,
  hideFallback,
  hideIframe,
  isExpandAvailable,
  setIframeHeight,
  showFallback,
  showIframe,
} from './iframe';
import {
  bindEscapeToClose,
  focusWidgetSurface,
  rememberFocus,
  restoreFocus,
  setOpenDialogAttrs,
} from './a11y';
import { postMessageToChild } from './postMessageToChild';
import { handleMessageFromChild } from './handleMessageFromChild';
import { createEventBus } from './events';
import { registerOpenTrigger } from './openTrigger';
import { collectSourceTrackingParams, hasAttributionSignal } from './sourceTracking';
import {
  attachLauncherButton,
  createLauncherButton,
  hideLauncherButton,
  isLauncherButtonShape,
  isWebchatPosition,
  setLauncherButtonColors,
  showLauncherButton,
} from './launcherButton';
import {
  clearDiagnosticsError,
  installDiagnostics,
  markDiagnosticsApiPresent,
  noteChatDisabled,
  noteIframeLoaded,
  noteReadyTimeout,
  peekScriptInstallHints,
  recordInitFailure,
  setDiagnosticsContext,
  setDiagnosticsError,
  setDiagnosticsState,
} from './diagnostics';
import {
  DEFAULT_LAUNCHER_BUTTON_SHAPE,
  DEFAULT_WEBCHAT_POSITION,
  DESKTOP_SIZE_TRANSITION_MS,
  READY_TIMEOUT_MS,
  SCRIPT_ATTR,
  SCRIPT_ELEMENT_ID,
  SIZE_TRANSITION_SETTLE_MS,
} from './constants';
import {
  WebChatInitError,
  type InitOptions,
  type LauncherButtonShape,
  type WebChatApi,
  type WebChatErrorDetail,
  type WebchatPosition,
} from './types';

// review: AMattRiley -- one generic helper instead of repeating the same validate-or-undefined
// ternary once per script-tag field below (webchatPosition, floatingButtonShape).
function validatedOrUndefined<T extends string>(
  isValid: (value: string | undefined) => value is T,
  raw: string | undefined,
): T | undefined {
  return isValid(raw) ? raw : undefined;
}

export function readInitOptionsFromScriptTag(): InitOptions {
  const hints = peekScriptInstallHints();

  if (!hints.tenantId || !hints.agentId) {
    throw new WebChatInitError(
      'MISSING_ATTRS',
      `web-chat-embed: could not find script tag #${SCRIPT_ELEMENT_ID} with required ${SCRIPT_ATTR.tenantId}/${SCRIPT_ATTR.agentId} attributes`,
    );
  }

  return {
    tenantId: hints.tenantId,
    agentId: hints.agentId,
    staticContainerId: hints.staticContainerId,
    affiliate: hints.affiliate,
    campaign: hints.campaign,
    // SETO-7698: tolerant-boolean -- anything other than the literal "true" is treated as absent.
    autoOpen: hints.autoOpen === 'true' ? true : undefined,
    floatingButton: hints.floatingButton === 'true' ? true : undefined,
    // SETO-7699: invalid/unrecognized values fall back to undefined here -- bootWidget applies
    // the actual 'right' default at the point the widget is built, not here.
    webchatPosition: validatedOrUndefined(isWebchatPosition, hints.webchatPosition),
    // SETO-7699 (icon-bubble addendum): same undefined-until-defaulted posture as
    // webchatPosition above -- bootWidget applies the actual 'label' default.
    floatingButtonShape: validatedOrUndefined(isLauncherButtonShape, hints.floatingButtonShape),
  };
}

// Synchronous: the loader makes no network call of its own (see iframe.ts) —
// the iframe src is derived directly from tenantId/agentId, so there's nothing
// to await before returning the API.
export function init(options: InitOptions): WebChatApi {
  // ESM consumers call init() without the UMD entry — install the dead-init-safe
  // __ST_WEBCHAT__ global here too (idempotent).
  installDiagnostics();

  const iframeUrl = buildIframeUrl(
    options.tenantId,
    options.agentId,
    collectSourceTrackingParams(options.affiliate, options.campaign),
  );
  const iframeOrigin = new URL(iframeUrl).origin;

  // Record caller-supplied IDs before attachIframe, which can throw (bad static container).
  setDiagnosticsContext({
    tenantId: options.tenantId,
    agentId: options.agentId,
    staticContainerId: options.staticContainerId,
    affiliate: options.affiliate,
    campaign: options.campaign,
    autoOpen: options.autoOpen ? 'true' : undefined,
    floatingButton: options.floatingButton ? 'true' : undefined,
    webchatPosition: options.webchatPosition,
    floatingButtonShape: options.floatingButtonShape,
    iframeSrc: iframeUrl,
    iframeOrigin,
  });

  try {
    return bootWidget(options, iframeUrl, iframeOrigin);
  } catch (error) {
    recordInitFailure(error);
    throw error;
  }
}

// review: AMattRiley -- single resolver for both webchatPosition and floatingButtonShape's
// undefined-until-defaulted values, instead of repeating the same `options.X ?? DEFAULT_X`
// pattern once per field in bootWidget below.
function resolveWidgetPresentation(options: InitOptions): {
  position: WebchatPosition;
  shape: LauncherButtonShape;
} {
  return {
    position: options.webchatPosition ?? DEFAULT_WEBCHAT_POSITION,
    shape: options.floatingButtonShape ?? DEFAULT_LAUNCHER_BUTTON_SHAPE,
  };
}

function bootWidget(options: InitOptions, iframeUrl: string, iframeOrigin: string): WebChatApi {
  // SETO-7699: one position drives the launcher button AND the chat window itself (iframe/
  // fallback, via showIframe/showFallback below) -- resolved once here so the whole widget always
  // floats together at the same corner, regardless of whether floatingButton is set.
  const { position, shape } = resolveWidgetPresentation(options);

  const iframe = createIframe(iframeUrl);
  // onClose is invoked later (button click), after api is assigned — same late-bind
  // as the webchat:close-request handler below.
  const fallback = createFallback(() => api.close());
  attachIframe(iframe, options.staticContainerId, fallback);

  // SETO-7699: independent host-page chrome, always document.body regardless of
  // options.staticContainerId (that escape hatch is about where the *iframe* mounts).
  const launcherButton = options.floatingButton ? createLauncherButton(position, shape) : null;
  if (launcherButton) {
    attachLauncherButton(launcherButton);
    // Safe to reference api here despite it being defined later below -- same reasoning as the
    // webchat:close-request handler further down: a click can only ever fire later, after this
    // synchronous function body (and so `api`) has already finished running.
    launcherButton.addEventListener('click', () => api.open());
  }

  setDiagnosticsState('waiting-ready');

  // Distinguish "iframe never loaded" (CSP/network) from "loaded but no ready"
  // (origin gate / config) — both currently end as READY_TIMEOUT without this.
  let iframeLoadSeen = false;
  iframe.addEventListener('load', () => {
    iframeLoadSeen = true;
    noteIframeLoaded();
  });
  iframe.addEventListener('error', () => {
    setDiagnosticsError('IFRAME_LOAD_FAILED', 'iframe element fired an error event');
  });

  const events = createEventBus();

  // Tracked so a timeout that elapses while the widget is already open can
  // swap the visible box live, and so a later open()/close() call knows
  // whether to show the iframe or the fallback in its place.
  let isOpen = false;
  let isExpanded = false;
  let suppressContentResize = false;
  let sizeTransitionTimer: ReturnType<typeof setTimeout> | undefined;
  // review: AMattRiley -- the latest webchat:resize height held back by suppressContentResize,
  // applied once the transition window ends. Without it, a child that reports its new height only
  // once, mid-restore, stays at the pre-toggle height until some unrelated later resize.
  let pendingContentHeight: number | undefined;
  // SETO-7715: single source of truth for why the fallback is showing instead of the iframe --
  // a READY_TIMEOUT_MS timeout and an explicit webchat:disabled are different reasons, but two
  // independent booleans for them can drift (e.g. one resets on recovery, the other doesn't).
  let fallbackReason: 'none' | 'timed-out' | 'disabled' = 'none';
  let childReady = false;
  // SETO-7698: monotonic, unlike isOpen -- set the first time open() ever runs and never reset by
  // close(). Guards auto-open against reopening a widget the visitor already opened AND closed via
  // a trigger click before the first ready arrived (isOpen alone would read false at that point,
  // wrongly signaling "never opened").
  let everOpened = false;
  // SETO-7346: element that held focus before the most recent closed→open transition.
  let previousFocus: Element | null = null;

  function showsFallback(): boolean {
    return fallbackReason !== 'none';
  }

  function syncLifecycleDiagState(): void {
    if (fallbackReason === 'disabled') {
      setDiagnosticsState('disabled');
      return;
    }
    if (fallbackReason === 'timed-out') {
      setDiagnosticsState('timed-out');
      return;
    }
    if (isOpen) {
      setDiagnosticsState('open');
      return;
    }
    if (childReady) {
      setDiagnosticsState('ready');
      return;
    }
    setDiagnosticsState('waiting-ready');
  }

  // SETO-7346: non-modal dialog attrs on the visible surface only (iframe or fallback).
  function syncOpenA11y(): void {
    if (!isOpen) {
      setOpenDialogAttrs(iframe, false);
      setOpenDialogAttrs(fallback, false);
      return;
    }

    if (showsFallback()) {
      setOpenDialogAttrs(iframe, false);
      setOpenDialogAttrs(fallback, true);
      return;
    }

    setOpenDialogAttrs(fallback, false);
    setOpenDialogAttrs(iframe, true);
  }

  function focusOpenSurface(): void {
    focusWidgetSurface(showsFallback() ? fallback : iframe);
  }

  // SETO-7699: hidden while the widget is open (so it never sits behind/beside the open iframe or
  // fallback at the same corner) OR while showsFallback() (review: AMattRiley -- without this, a
  // ready-timeout/webchat:disabled that arrives before the visitor ever opens the widget left the
  // button sitting there indefinitely on its placeholder color, clickable into a broken/empty
  // fallback panel instead of a real chat). Restored once neither condition holds -- on close, or
  // once a late webchat:ready calls recoverFromFallback (never for 'disabled', which by
  // construction has no recovery path: the child never sends webchat:ready once it's sent
  // webchat:disabled).
  function syncLauncherButtonVisibility(): void {
    if (!launcherButton) return;
    if (isOpen || showsFallback()) {
      hideLauncherButton(launcherButton);
    } else {
      showLauncherButton(launcherButton);
    }
  }

  let readyTimer: ReturnType<typeof setTimeout>;

  // Idempotent: a repeated or late-arriving signal (e.g. webchat:disabled arriving after
  // READY_TIMEOUT_MS already fired) must not re-emit 'error' or re-touch the DOM. buildDetail is
  // a thunk, not a precomputed value, so a no-op call never overwrites diagnostics with a signal
  // we otherwise ignored.
  function enterFallback(
    reason: 'timed-out' | 'disabled',
    buildDetail: () => WebChatErrorDetail,
  ): void {
    if (fallbackReason !== 'none') return;
    clearTimeout(readyTimer);
    fallbackReason = reason;
    // review: AMattRiley -- the child is no longer the visible surface, so an in-flight
    // expand/restore transition has nothing left to guard. isExpanded stays: the fallback takes the
    // same box (showFallback mirrors showIframe), so the swap does not snap the box down.
    cancelSizeTransition();
    const detail = buildDetail();
    syncLifecycleDiagState();
    syncLauncherButtonVisibility();
    if (isOpen) {
      hideIframe(iframe);
      showFallback(fallback, position, { expanded: isExpanded });
      syncOpenA11y();
      focusOpenSurface();
    }
    events.emit('error', detail);
  }

  // Undoes enterFallback -- a late webchat:ready recovers from either reason the same way. The
  // recovered child's expand state is unknown, so recovery starts from the default box; the child
  // re-sends expand-request if it wants the larger one.
  function recoverFromFallback(): void {
    if (fallbackReason === 'none') return;
    fallbackReason = 'none';
    isExpanded = false;
    hideFallback(fallback);
    syncLauncherButtonVisibility();
    if (isOpen) {
      showIframe(iframe, position, { expanded: isExpanded });
      syncOpenA11y();
      focusOpenSurface();
    }
  }

  // web-chat-ui never responded within the deadline (down, blocked by the
  // tenant's own CSP, wrong origin, etc.) — swap in the fallback wherever the
  // iframe would otherwise be visible. Cleared below if webchat:ready still
  // arrives late.
  readyTimer = setTimeout(() => {
    enterFallback('timed-out', () => noteReadyTimeout(iframeLoadSeen));
  }, READY_TIMEOUT_MS);

  function applyContentHeight(height: number): void {
    // Expanded size is a named box, not content-driven. A stale ResizeObserver
    // report must not shrink the taller iframe. Hold back while the expand/restore
    // CSS transition is running, or intermediate heights fight it.
    if (isExpanded) return;
    if (suppressContentResize) {
      pendingContentHeight = height;
      return;
    }
    setIframeHeight(iframe, height);
  }

  function endSizeTransition(): void {
    suppressContentResize = false;
    sizeTransitionTimer = undefined;
    const height = pendingContentHeight;
    pendingContentHeight = undefined;
    if (height !== undefined) applyContentHeight(height);
  }

  // Drops the transition and anything it held back, for when the iframe stops being the open
  // surface (close, fallback).
  function cancelSizeTransition(): void {
    if (sizeTransitionTimer !== undefined) clearTimeout(sizeTransitionTimer);
    sizeTransitionTimer = undefined;
    suppressContentResize = false;
    pendingContentHeight = undefined;
  }

  // SETO-7661 (review: seanw1023): the child cannot see the host page from inside a cross-origin
  // iframe, so it cannot know whether there is room to grow -- see isExpandAvailable(). This
  // loader answers for it, on the first ready and again whenever the answer changes. Nothing is
  // sent before the child is ready: there would be no listener yet, and no queue on either side.
  // Undefined (not false) to start, so the very first ready always reports, even a false one.
  let expandAvailable: boolean | undefined;

  function syncExpandAvailability(): void {
    if (!childReady) return;
    const available = isExpandAvailable();
    if (available === expandAvailable) return;
    expandAvailable = available;
    // Losing the room while expanded collapses the state on this side too, not just the painted
    // box: showIframe already falls back to the default box when the expanded one does not fit,
    // but leaving isExpanded set would silently re-expand on the next resize that happens to fit
    // again -- out of step with the child, which clears its own expand state on this same message.
    if (!available) isExpanded = false;
    postMessageToChild(iframe, iframeOrigin, {
      type: WEBCHAT_EXPAND_AVAILABILITY,
      available,
    });
  }

  // SETO-7661 (review: seanw1023): drop a request we cannot act on instead of recording it. The
  // flag lives until close(), so latching it while closed -- a stray or late expand-request from
  // the child's own teardown, after close() but before the next open() -- would silently reopen at
  // the expanded box, contradicting the "close() restores the default size" contract in docs/api.md.
  // Same for a request arriving while the fallback is up: recovery re-mounts a fresh child, whose
  // expand state we have no reason to assume. Nothing is lost either way -- the child re-sends
  // expand-request whenever it actually wants the larger box.
  //
  // review: AMattRiley -- and before the first webchat:ready. An out-of-order request would latch
  // isExpanded and start the transition timer for a child that may never become ready.
  function applyExpandRequest(expanded: boolean): void {
    if (!isOpen || !childReady || fallbackReason !== 'none') return;
    isExpanded = expanded;
    suppressContentResize = true;
    if (sizeTransitionTimer !== undefined) clearTimeout(sizeTransitionTimer);
    sizeTransitionTimer = setTimeout(
      endSizeTransition,
      DESKTOP_SIZE_TRANSITION_MS + SIZE_TRANSITION_SETTLE_MS,
    );
    showIframe(iframe, position, { expanded: isExpanded, animateSize: true });
  }

  handleMessageFromChild([iframeOrigin], (message) => {
    if (message.type === WEBCHAT_MESSAGE_TYPE.READY) {
      clearTimeout(readyTimer);
      // SETO-7698: captured before childReady flips below, so this is true only on the very
      // first ready this init() lifecycle ever sees -- a later re-emission (e.g. the
      // recoverFromFallback late-recovery path just below) must not reopen a widget the visitor
      // has since closed.
      const isFirstReady = !childReady;
      childReady = true;
      clearDiagnosticsError();
      // SETO-7701: no independent hex/luminance computation here -- web-chat-ui already resolved
      // a readable foreground for this background (against the visitor's prefers-color-scheme,
      // client-side there). Absent on a webchat:ready with no theme to give (e.g.
      // DisabledChatNotice) -- the button just keeps its placeholder fill.
      if (launcherButton && message.headerColors) {
        setLauncherButtonColors(
          launcherButton,
          message.headerColors.background,
          message.headerColors.foreground,
        );
      }
      recoverFromFallback();
      syncExpandAvailability();
      syncLifecycleDiagState();
      // SETO-7318: a trigger click firing before the iframe's own app has mounted and registered
      // its message listener sends webchat:open into the void -- there's no queue on either side,
      // so it's silently dropped forever and the box is left open but empty. Replaying it here,
      // once the child confirms (via webchat:ready) that a listener now exists, recovers that
      // race. Harmless outside the race window too: if ready already arrived before open() was
      // ever called, this branch doesn't run again, so nothing gets double-sent.
      if (isOpen) {
        postMessageToChild(iframe, iframeOrigin, { type: WEBCHAT_MESSAGE_TYPE.OPEN });
      }

      // SETO-7267: re-collect and re-send once the child is confirmed ready — covers values that
      // weren't there yet when the initial iframe src (above) was built, e.g. the tenant page's
      // own marketing-attribution script writing sessionStorage after this script ran.
      const sourceTrackingParams = collectSourceTrackingParams(options.affiliate, options.campaign);
      if (hasAttributionSignal(sourceTrackingParams)) {
        postMessageToChild(iframe, iframeOrigin, {
          type: WEBCHAT_MESSAGE_TYPE.SOURCE_TRACKING,
          params: sourceTrackingParams,
        });
      }

      // SETO-7698: everOpened (not isOpen) -- skips auto-open both when currently open (a trigger
      // click landed before this first ready; avoids re-running open()'s side effects or
      // double-sending webchat:open on top of the SETO-7318 replay above) AND when the visitor
      // already opened-then-closed it before ready ever arrived, where isOpen alone would read
      // false and wrongly auto-open a widget they deliberately dismissed.
      //
      // SETO-7735: fires regardless of floatingButton -- previously the button suppressed this
      // entirely (SETO-7699's original "button wins" design), but api.open() already hides the
      // button via syncLauncherButtonVisibility() once isOpen flips true, so there's no visible
      // conflict between the two: the widget opens immediately, and the button that would have
      // been the manual trigger just isn't shown until the visitor closes it again.
      if (isFirstReady && !everOpened && options.autoOpen) {
        api.open();
      }

      events.emit('ready');
    }
    if (message.type === WEBCHAT_MESSAGE_TYPE.DISABLED) {
      enterFallback('disabled', noteChatDisabled);
    }
    if (message.type === WEBCHAT_MESSAGE_TYPE.RESIZE) {
      applyContentHeight(message.height);
    }
    if (message.type === WEBCHAT_MESSAGE_TYPE.EXPAND_REQUEST) {
      applyExpandRequest(message.expanded);
    }
    // SETO-7273: the widget's own close button. Reuses api.close() as-is (defined below, but
    // safe to reference here -- this callback only ever runs later, on an actual future message
    // event, by which point api has long since been assigned) so a click inside the widget
    // collapses the iframe exactly like the host page's own close() already does, right down to
    // the outbound webchat:close echo and the 'close' event listeners fire either way.
    if (message.type === WEBCHAT_MESSAGE_TYPE.CLOSE_REQUEST) {
      api.close();
    }
  });

  // SETO-7278: re-applies whichever box (iframe or fallback) is currently meant to be visible --
  // shared by open() and the resize listener below, so crossing the mobile breakpoint live
  // (device rotation, a desktop window resize) re-lays-out the same way opening fresh would.
  function showCurrentBox(): void {
    if (showsFallback()) {
      showFallback(fallback, position, { expanded: isExpanded });
    } else {
      showIframe(iframe, position, { expanded: isExpanded });
    }
  }

  // review: AMattRiley -- availability is gated on isOpen too. close() clears expandAvailable so
  // the next open() re-tells the child; syncing here while closed would refill it and turn that
  // re-tell into a silent no-op.
  function reapplyBoxWhileOpen(): void {
    if (!isOpen) return;
    syncExpandAvailability();
    showCurrentBox();
  }

  // review: AMattRiley -- iOS fires visualViewport scroll continuously while the on-screen keyboard
  // animates and during momentum scrolling. Each pass reads layout and writes iframe/backdrop
  // styles, and only the latest viewport matters, so a burst collapses into one pass per frame.
  let reapplyFrame: number | undefined;
  function scheduleReapply(): void {
    if (reapplyFrame !== undefined) return;
    reapplyFrame = window.requestAnimationFrame(() => {
      reapplyFrame = undefined;
      reapplyBoxWhileOpen();
    });
  }

  window.addEventListener('resize', scheduleReapply);
  // SETO-7774: the on-screen keyboard shrinks and pans the *visual* viewport only, and iOS
  // reports that solely through these two events (no window resize). Re-applying the mobile box
  // keeps the open widget sized to the visible area above the keyboard -- see fillMobileViewport.
  // Bound once, like the resize listener above, and a no-op while closed.
  window.visualViewport?.addEventListener('resize', scheduleReapply);
  window.visualViewport?.addEventListener('scroll', scheduleReapply);

  const api: WebChatApi = {
    open(): void {
      // SETO-7346: only capture the pre-open focus on a closed→open transition.
      if (!isOpen) {
        previousFocus = rememberFocus();
      }
      isOpen = true;
      everOpened = true;
      showCurrentBox();
      syncOpenA11y();
      syncLauncherButtonVisibility();
      focusOpenSurface();
      syncLifecycleDiagState();
      postMessageToChild(iframe, iframeOrigin, { type: WEBCHAT_MESSAGE_TYPE.OPEN });
      // Close clears expandAvailable so this open re-tells the child. The child resets
      // hostCanExpand on webchat:close and has no other way to learn the host still has room.
      syncExpandAvailability();
      events.emit('open');
    },
    close(): void {
      const wasOpen = isOpen;
      isOpen = false;
      isExpanded = false;
      expandAvailable = undefined;
      cancelSizeTransition();
      hideIframe(iframe);
      hideFallback(fallback);
      syncOpenA11y();
      syncLauncherButtonVisibility();
      syncLifecycleDiagState();
      postMessageToChild(iframe, iframeOrigin, { type: WEBCHAT_MESSAGE_TYPE.CLOSE });
      events.emit('close');
      // SETO-7346: restore focus only when we actually transitioned open→closed.
      if (wasOpen) {
        restoreFocus(previousFocus);
        previousFocus = null;
      }
    },
    addEventListener: events.addEventListener,
    removeEventListener: events.removeEventListener,
  };

  // SETO-7346: Escape closes while open (host page — fallback / focus still on host).
  // Keydowns inside the cross-origin iframe do not reach this listener; web-chat-ui
  // handles Escape there and posts webchat:close-request (same as the close button).
  bindEscapeToClose(() => isOpen, api.close);

  registerOpenTrigger(api.open);
  markDiagnosticsApiPresent();

  return api;
}
